import { useState, type KeyboardEvent } from 'react';
import { api, get, post } from '../api';
import { Problems } from './Field';
import { dayLabel, todayLocal } from '../dates';
import { describe } from '../errors';
import { useLoad } from '../hooks';

interface Skip {
  date: string;
  reason: string;
}

/**
 * Dates this assignment does not run on (the customer is away). Skipping hides a
 * visit already booked, unless the office changed it by hand; un-skipping brings
 * it back. It sits inside the assignment form but apart from its Save: each change
 * takes effect straight away, so Enter in its fields adds the date rather than
 * submitting the assignment.
 */
export function SkipDates(props: { assignmentId: number | string }) {
  const [tick, setTick] = useState(0);
  const list = useLoad(
    () => get<{ items: Skip[] }>(`/api/assignments/${props.assignmentId}/skips`),
    [props.assignmentId, tick],
  );
  const [date, setDate] = useState(todayLocal());
  const [reason, setReason] = useState('');
  const [problems, setProblems] = useState<string[]>([]);
  const [message, setMessage] = useState('');
  const items = Array.isArray(list.data?.items) ? list.data!.items : [];

  async function add() {
    setProblems([]);
    setMessage('');
    try {
      const r = await post<{ hidden: number; kept: number }>(
        `/api/assignments/${props.assignmentId}/skips`,
        { date, reason },
      );
      setReason('');
      setMessage(
        [
          r.hidden > 0
            ? `${r.hidden} booked ${r.hidden === 1 ? 'visit is' : 'visits are'} now hidden.`
            : 'Added.',
          r.kept > 0
            ? `${r.kept} ${r.kept === 1 ? 'visit was' : 'visits were'} left alone because you had changed ${r.kept === 1 ? 'it' : 'them'} by hand or ${r.kept === 1 ? 'it is' : 'they are'} already on an invoice or bill. Cancel ${r.kept === 1 ? 'it' : 'them'} from the schedule if needed.`
            : '',
        ]
          .filter(Boolean)
          .join(' '),
      );
      setTick((t) => t + 1);
    } catch (err) {
      setProblems(describe(err).problems);
    }
  }

  async function remove(s: Skip) {
    setProblems([]);
    setMessage('');
    try {
      await api('DELETE', `/api/assignments/${props.assignmentId}/skips/${s.date}`);
      setMessage(`Running again on ${dayLabel(s.date)}.`);
      setTick((t) => t + 1);
    } catch (err) {
      setProblems(describe(err).problems);
    }
  }

  return (
    <section className="stack narrow" aria-labelledby="skips-h">
      <h2 id="skips-h">Skip these dates</h2>
      <p className="muted">
        No visit is made on a skipped date. Visits you have already changed by hand are kept.
        Changes here are saved straight away.
      </p>
      <Problems problems={problems} title="Could not do that." />
      {message && (
        <p className="notice" role="status">
          {message}
        </p>
      )}
      {items.length === 0 && list.data && <p className="empty">No skipped dates.</p>}
      <ul className="plain">
        {items.map((s) => (
          <li key={s.date} className="team-row">
            <span>
              {dayLabel(s.date)}
              {s.reason && <span className="muted"> — {s.reason}</span>}
            </span>
            <button type="button" className="danger" onClick={() => void remove(s)}>
              Remove<span className="sr-only"> skip on {dayLabel(s.date)}</span>
            </button>
          </li>
        ))}
      </ul>
      <div
        className="inline"
        role="group"
        aria-label="Add a skip date"
        onKeyDown={(e: KeyboardEvent) => {
          if (e.key === 'Enter' && e.target instanceof HTMLInputElement) {
            e.preventDefault();
            void add();
          }
        }}
      >
        <label>
          Date to skip
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
        </label>
        <label>
          Reason (optional)
          <input value={reason} maxLength={200} onChange={(e) => setReason(e.target.value)} />
        </label>
        <button type="button" onClick={() => void add()}>
          + Add skip date
        </button>
      </div>
    </section>
  );
}
